import React, { useEffect, useRef, useState } from 'react';
import { Grid, Flex, Text, Heading, Box, ThemeUIStyleObject } from 'theme-ui';
import CTAButtonWithWrapper from '~/components/Generic/CTAButtonWithWrapper';
import ImgLoader from '~/components/Generic/ImgLoader';
import LinkRenderer from '~/components/Generic/LinkRenderer';
import BadgeHighlight from './BadgeHighlight';
import LiteCarousel from '~/components/Generic/LiteCarousel';
import useMatchMedia from '~/hooks/utils/use-match-media';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { GVestSport } from '~/utils/g-vest-sport-font-weight';

type Props = {
  card: Queries.HomeCollectionCardFragmentFragment;
  index: number;
  isCollectionModule: boolean;
  forwardSx?: ThemeUIStyleObject;
};

const FeaturedCard = ({ card, index, isCollectionModule, forwardSx }: Props) => {
  const {
    image,
    images,
    mobileImage,
    mobileImages,
    CTA,
    description,
    title,
    secondaryTitle,
    badgeHighlights,
    titleColor,
  } = card;

  const isPaused = useRef(false);
  const baseAutoplaySpeed = 5000;
  const slideAutoplaySpeed = baseAutoplaySpeed + index * 1000;
  const [autoplaySpeed, setAutoplaySpeed] = useState<number | null>(slideAutoplaySpeed);

  const columnPosition = getColumnPosition(index, isCollectionModule);
  const rowPosition = getRowPosition(index, isCollectionModule);
  const mobileImageHeight = getMobileImageHeight(isCollectionModule);
  const desktopImageHeight = getDesktopImageHeight(isCollectionModule);

  const slideCallback = (image: { readonly id: string; readonly gatsbyImageData: IGatsbyImageData | null }) => (
    <Box key={image?.id ?? ''} sx={{ height: '100%', width: '100%', isolation: 'isolate' }}>
      <ImgLoader
        image={image}
        forwardSx={{
          width: '100%',
          height: '100%',
          maxHeight: '66.8rem',
          WebkitMaskImage: '-webkit-radial-gradient(white, black)',
        }}
        objectFit="cover"
        objectPosition="top"
      />
    </Box>
  );
  const isMobile = useMatchMedia('mobile');
  const isTablet = useMatchMedia('tablet');
  const isMobileOrTablet = isMobile || isTablet;
  const slides = isMobileOrTablet
    ? mobileImages?.map((image) => image && slideCallback(image))
    : images?.map((image) => image && slideCallback(image));

  /**
   * useEffect to stagger transitions
   * First transition is 2.5s + index in seconds (slidAutoplaySpeed)
   * Then pause for 2.5s and transition again (baseAutoplaySpeed)
   */
  useEffect(() => {
    let timeoutId1: NodeJS.Timeout | null = null;
    let timeoutId2: NodeJS.Timeout | null = null;

    if (!isPaused.current && autoplaySpeed) {
      timeoutId1 = setTimeout(() => {
        isPaused.current = true;
        setAutoplaySpeed(null);
      }, autoplaySpeed);
    }

    if (isPaused.current) {
      timeoutId2 = setTimeout(() => {
        isPaused.current = false;
        setAutoplaySpeed(baseAutoplaySpeed);
      }, baseAutoplaySpeed);
    }
    return () => {
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
    };
  }, []);

  return (
    <Grid
      sx={{
        gridRow: ['auto', isCollectionModule ? null : rowPosition, rowPosition],
        gridColumn: [1, isCollectionModule ? null : columnPosition, columnPosition],
        gridGap: ['1.2rem', '2.4rem'],
        overflow: 'hidden',
        gridTemplateRows: [`${mobileImageHeight} 1fr`, `${desktopImageHeight} 1fr`],
        position: 'relative',
        WebkitMaskImage: '-webkit-radial-gradient(white, black)',
        backgroundColor: isCollectionModule ? 'white' : '#f8f8f8',
        ...forwardSx,
      }}
    >
      {isCollectionModule ? (
        // @ts-ignore
        <LinkRenderer
          slug={CTA?.page?.slug}
          page={CTA?.page ?? {}}
          innerLink={CTA?.innerLink}
          href={CTA?.url}
          title={CTA?.page?.title ?? CTA?.name}
          type={CTA?.page?.__typename}
          locationDetail={'featured_card'}
          sx={{
            display: 'block',
            position: 'relative',
          }}
        >
          {!!images?.length ? (
            //@ts-ignore
            <LiteCarousel
              arrows={false}
              dots={false}
              autoplaySpeed={autoplaySpeed}
              slides={slides}
              forwardSx={{ width: '100%', height: '100%' }}
            />
          ) : (
            <ImgLoader image={image} forwardSx={{ width: '100%', height: '100%' }} />
          )}
          {isCollectionModule && title ? (
            <Box
              as="h3"
              sx={{
                color: titleColor ?? 'white',
                fontSize: ['5rem', '8rem'],
                textTransform: 'uppercase',
                lineHeight: 0.85,
                position: 'absolute',
                fontWeight: 'bold',
                bottom: ['18px', '30px'],
                right: ['6px', '20px'],
                transform: 'rotate(180deg)',
                writingMode: 'vertical-rl',
              }}
            >
              <GVestSport text={title} />
            </Box>
          ) : null}
        </LinkRenderer>
      ) : (
        <>
          <ImgLoader
            image={image}
            forwardSx={{
              display: [mobileImage ? 'none' : 'block', 'block'],
              borderRadius: '16px',
              width: '100%',
              height: '100%',
            }}
            objectFit="cover"
            objectPosition="top"
          />
          {mobileImage && (
            <ImgLoader
              image={mobileImage}
              forwardSx={{
                display: ['block', 'none'],
                borderRadius: '16px',
                width: '100%',
                height: '100%',
              }}
              objectFit="cover"
              objectPosition="top"
            />
          )}
        </>
      )}

      <Grid
        sx={{
          gridTemplateRows: 'minmax(auto, 1fr) 1fr auto',
          px: isCollectionModule ? ['1.6rem', '2.4rem'] : 0,
          pb: isCollectionModule ? ['1.6rem', '2.4rem'] : 0,
          gridGap: '.8rem',
        }}
      >
        <Flex
          sx={{
            gap: ['1.6rem', isCollectionModule ? '1.2rem' : '2.4rem'],
            flexDirection: ['column-reverse', isCollectionModule ? 'row' : 'column-reverse'],
            justifyContent: ['flex-start', isCollectionModule ? 'space-between' : 'flex-start'],
          }}
        >
          {isCollectionModule
            ? !!secondaryTitle && (
                <Heading
                  variant="h5"
                  as="h4"
                  sx={{
                    fontSize: ['2.4rem', '2.8rem'],
                    textTransform: 'uppercase',
                    letterSpacing: '-0.02em',
                    lineHeight: 1,
                  }}
                  dangerouslySetInnerHTML={{ __html: secondaryTitle }}
                />
              )
            : !!title && (
                <Heading
                  variant="h5"
                  as="h3"
                  sx={{
                    fontSize: ['2.4rem', '2.8rem'],
                    textTransform: 'uppercase',
                    letterSpacing: '-0.02em',
                    lineHeight: 1,
                  }}
                  dangerouslySetInnerHTML={{ __html: title }}
                />
              )}

          <Flex sx={{ gap: '1.2rem', flexShrink: 0 }}>
            {Boolean(badgeHighlights?.length) &&
              badgeHighlights?.map((text, index) => (
                <BadgeHighlight key={`${text}-${index}`} text={text ?? ''} isCollectionModule={isCollectionModule} />
              ))}
          </Flex>
        </Flex>
        {description?.childMarkdownRemark?.html ? (
          <Text
            sx={{
              mb: '0.8rem',
              fontSize: '1.7rem',
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              '& > p': { m: 0 },
              maxWidth: '60ch',
              alignSelf: 'start',
            }}
            dangerouslySetInnerHTML={{
              __html: description.childMarkdownRemark.html,
            }}
          />
        ) : null}
        {CTA && (
          //@ts-ignore
          <CTAButtonWithWrapper
            key={CTA.id}
            locationDetail={'featured_card'}
            aria-label={`Navigate to ${CTA?.page?.slug ?? CTA.text}`}
            callToAction={CTA}
            forwardSx={{ alignSelf: 'end' }}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default FeaturedCard;

// Utils specific to this component
const getColumnPosition = (index: number, isCollectionModule: boolean) => {
  if (isCollectionModule) {
    switch (index) {
      case 0:
        return '1/2';
      case 1:
        return '2/3';
      case 2:
        return '3/4';
      default:
        break;
    }
  }
  switch (index) {
    case 0:
      return '1/4';
    case 1:
      return '4/6';
    case 2:
      return '1/3';
    case 3:
      return '3/6';
    default:
      break;
  }
};

const getRowPosition = (index: number, isCollectionModule: boolean) => {
  if (isCollectionModule) return 2;
  switch (index) {
    case 0:
    case 1:
      return 2;
    case 2:
    case 3:
      return 3;
    default:
      break;
  }
};

const getMobileImageHeight = (isCollectionModule: boolean) => {
  if (isCollectionModule) return '36.6rem';
  return '23.2rem';
};
const getDesktopImageHeight = (isCollectionModule: boolean) => {
  if (isCollectionModule) return '49.6rem';
  return '49.6rem';
};
